import { PERIOD_PRESETS, resolvePeriod, type PeriodPreset } from '../analytics/period';

/**
 * List filters kept in the URL (UX-022): a link or "back" lands on the same filtered list.
 * Pure helpers shared by every FilterBar; the screens only declare which keys they use.
 */
export interface FilterDef {
  key: string;
  /** Several values in one parameter, comma separated (`estado=ASSIGNED,ACCEPTED`). */
  multi?: boolean;
  /** Value when the parameter is missing; it is never written to the URL. */
  default?: string;
  /** Not counted as an active filter (search, period, sort): they have their own controls. */
  primary?: boolean;
}

export type FilterValues = Record<string, string | string[] | undefined>;
/** Query parameters as pairs (`URLSearchParams` is one): the domain stays free of browser types. */
export type ParamPairs = Iterable<readonly [string, string]>;
export interface ParamReader {
  get(key: string): string | null;
}

/** Parameters that go back to the first page whenever a filter changes. */
const PAGE_KEYS = ['pagina', 'page'];

export function readFilters(params: ParamReader, defs: FilterDef[]): FilterValues {
  const out: FilterValues = {};
  for (const d of defs) {
    const raw = params.get(d.key) ?? d.default;
    if (raw === undefined || raw === '') {
      out[d.key] = d.multi ? [] : undefined;
      continue;
    }
    out[d.key] = d.multi ? raw.split(',').filter(Boolean) : raw;
  }
  return out;
}

/** New parameters with `patch` applied; empty or default values are removed and paging restarts. */
export function writeFilters(params: ParamPairs, defs: FilterDef[], patch: FilterValues): Array<[string, string]> {
  const next = new Map<string, string>(params);
  for (const [key, value] of Object.entries(patch)) {
    const def = defs.find((d) => d.key === key);
    const text = Array.isArray(value) ? value.join(',') : (value ?? '');
    if (!text || text === def?.default) next.delete(key);
    else next.set(key, text);
  }
  for (const k of PAGE_KEYS) next.delete(k);
  return [...next];
}

/** Keys of the secondary filters that are set (drives the chips and the «Más filtros» badge). */
export function activeFilterKeys(values: FilterValues, defs: FilterDef[]): string[] {
  return defs
    .filter((d) => !d.primary)
    .filter((d) => {
      const v = values[d.key];
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== '' && v !== d.default;
    })
    .map((d) => d.key);
}

/** Every filter back to its default, keeping unrelated parameters (the open record, the tab). */
export function clearFilters(params: ParamPairs, defs: FilterDef[], keys = defs.map((d) => d.key)): Array<[string, string]> {
  return writeFilters(params, defs, Object.fromEntries(keys.map((k) => [k, undefined])));
}

// ---------------------------------------------------------------- period
export interface PeriodValue {
  preset: PeriodPreset;
  /** YYYY-MM-DD, only for `custom`. */
  from?: string;
  to?: string;
}

export const isPeriodPreset = (v: unknown): v is PeriodPreset => typeof v === 'string' && (PERIOD_PRESETS as readonly string[]).includes(v);

/**
 * The period as list endpoints take it: `from` inclusive and `to` exclusive instants (ISO),
 * the local days of the tenant. A custom range missing a day falls back to `fallback`.
 */
export function periodRange(value: PeriodValue, now: Date, timeZone: string, fallback: PeriodPreset = 'last7'): { from: string; to: string; label: string } {
  const custom = value.preset === 'custom';
  const usable = !custom || (!!value.from && !!value.to && value.from <= value.to);
  const resolve = (input: Parameters<typeof resolvePeriod>[0]) => resolvePeriod(input, now, timeZone);
  let p;
  try {
    p = resolve(usable ? { preset: value.preset, from: value.from, to: value.to, compare: 'none' } : { preset: fallback, compare: 'none' });
  } catch {
    // a hand-edited URL (range too long, malformed day) shows the default period, not an error
    p = resolve({ preset: fallback, compare: 'none' });
  }
  return { from: p.current.start, to: p.current.end, label: p.label };
}
